/**
 * CarritoView -- Carrito de ventas con productos sincronizados de Desktop.
 * Permite armar pedidos y enviarlos para que Desktop los procese.
 * Artefacto: #/carrito
 * Mision: PANEL-OPERATIONS-001 T2
 *
 * @module presentation/views/CarritoView
 */

import { ApiBase } from '../../infrastructure/api/ApiBase.js';
import { AppConfig } from '../../config.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { getOperador } from '../components/OperadorPin.js';
import { OfflineQueue } from '../../infrastructure/offline/OfflineQueue.js';
import { setPageTitle } from '../components/NavBar.js';
import { negocioDeLaSubida, MENSAJE_SIN_NEGOCIO } from '../../application/sync/negocioDeLaSubida.js';
import { MENSAJE_SIN_MESA, lineasDeConfirmacion } from '../../application/carrito/confirmacionPedido.js';
import { normalizarMesas, mesaAbierta, lineaDeMesa, adicionDe } from '../../application/carrito/mesasAbiertas.js';
import { interpretarCantidad } from '../../application/carrito/cantidadDeLinea.js';
import { llevaStock, estadoDeStock } from '../../application/carrito/stockDelProducto.js';
import { agregarUnidad, quitarUnidad, unidadesDe, lineaPorLid, cantidadDeLinea, quitarLinea, separarUnidad, detalleDelPedido } from '../../application/carrito/lineasDelPedido.js';
// T6 PRODUCTOS-SYNC-POSTGRES-V1: cliente REST canon v1.2 para productos.
// Mientras Ares aplica las migrations 01-04 al VPS, el catalogo usa fallback
// al endpoint legacy /api/sync/productos. Cuando productos exista en BD el
// REST devuelve items y el fallback no se activa -- sin cambio de codigo.
import { ProductosApiClient } from '../../infrastructure/api/ProductosApiClient.js';
import { avisar } from '../shared/nxDialogo.js';

function isBusinessUser() {
  const locked = localStorage.getItem('nx_business_locked') === 'true';
  const role = localStorage.getItem('nx_role') || 'dev';
  return locked && role !== 'dev' && role !== 'admin';
}

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';
const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

let cart = [];
// KV2-T2: mesa del pedido — numero de mesa (string) o 'LLEVAR' (para llevar). null = sin elegir.
let mesaSel = null;
// CARRITO-MESAS-ADICIONES-T1 paso 2: las mesas que sube el escritorio (GET /api/sync/mesas), con 30 s de vida.
// null = sin consultar o sin dato (fail-open: el carrito sigue como hoy).
let mesasCache = { biz: null, at: 0, mesas: null };
async function cargarMesas(bizId) {
  if (!bizId) return null;
  if (mesasCache.biz === bizId && Date.now() - mesasCache.at < 30000) return mesasCache.mesas;
  try {
    const res = await fetchSyncData('mesas', { businessId: bizId, pageSize: 200 });
    const items = res?.ok ? (res.data?.data?.items || res.data?.items || []) : null;
    mesasCache = { biz: bizId, at: Date.now(), mesas: items ? normalizarMesas(items) : null };
  } catch {
    mesasCache = { biz: bizId, at: Date.now(), mesas: null };
  }
  return mesasCache.mesas;
}
// KV2-T3: nota general del pedido (las notas por producto viven en cart[i].nota).
let pedidoNota = '';
let currentTab = 'productos';
let lastScanAt = 0;                   // T1: timestamp ultimo scan (evita duplicados accidentales)
const SCAN_DUP_WINDOW_MS = 600;       // scanner USB suele disparar rafagas < 400ms

// ===== KV2 mockup v2 (ASSIGN #2833, artifact 1f82f29d aprobado) =====
// Navegacion del catalogo: entrada por CATEGORIAS (grid con conteo) -> productos de
// la categoria. El buscador salta directo a resultados globales (cross-categoria).
let catalogoModo = 'categorias';      // 'categorias' | 'productos'
let categoriaActiva = null;           // grupo seleccionado ('' = Todos)

// PEDIDOS RECIENTES (hoy, por negocio): registro client-side al enviar + cruce con
// GET /api/sync/ordenes para confirmar que la orden llego a la nube. El estado
// "ya descargado por Desktop" NO es derivable hoy (sync_ordenes no tiene flag de
// descarga) — cuando el backend lo exponga se agrega el tercer estado sin re-disenar.
const RECENT_KEY_PREFIX = 'nx_cart_recent_';
function recentKey(bizId) { return RECENT_KEY_PREFIX + (bizId || 'default'); }
function loadRecientes(bizId) {
  try {
    const all = JSON.parse(localStorage.getItem(recentKey(bizId)) || '[]');
    const hoy = new Date().toDateString();
    return all.filter(r => new Date(r.fecha).toDateString() === hoy);
  } catch { return []; }
}
function pushReciente(bizId, orden, estadoLocal) {
  try {
    const hoy = loadRecientes(bizId);
    hoy.unshift({
      id: orden.id, fecha: orden.fecha, hora: orden.hora,
      mesa: orden.para_llevar ? 'LLEVAR' : orden.mesa,
      vendedor: orden.vendedor, total: orden.total,
      unidades: orden.units_count, estadoLocal // 'enviado' | 'encolado'
    });
    localStorage.setItem(recentKey(bizId), JSON.stringify(hoy.slice(0, 30)));
  } catch { /* quota — recientes es best-effort */ }
}

/** Resalta coincidencias (client-side) respetando HTML ya escapado. */
function highlight(text, term) {
  if (!term) return esc(text);
  const escaped = esc(text);
  const needle = esc(term).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (!needle) return escaped;
  return escaped.replace(new RegExp(needle, 'gi'), m => `<span class="ops-highlight">${m}</span>`);
}

async function fetchSyncData(table, opts = {}) {
  const params = new URLSearchParams();
  if (opts.businessId) params.set('businessId', opts.businessId);
  if (opts.search) params.set('search', opts.search);
  params.set('page', opts.page || 1);
  params.set('pageSize', opts.pageSize || 200);
  return ApiBase.get(`/api/sync/${table}?${params}`);
}

function extractProduct(item) {
  const d = item.data || {};
  return {
    id: d.Codigo || d.codigo || d.id || d.ID || item.rowId || '',
    nombre: d.Producto || d.producto || d.nombre || d.Nombre || '(sin nombre)',
    precio: parseFloat(d.Precio || d.precio || d.precio_venta || d.PrecioVenta || 0),
    stock: parseInt(d.Inventario ?? d.inventario ?? d.cantidad ?? d.stock ?? 0),
    llevaStock: llevaStock(d),
    grupo: d.Grupo || d.grupo || '',
    costo: parseFloat(d.Costo || d.costo || 0)
  };
}

function getCartSummary() {
  const items = cart.length;
  const units = cart.reduce((s, i) => s + i.qty, 0);
  const total = cart.reduce((s, i) => s + i.precio * i.qty, 0);
  return { items, units, total };
}

// CARRITO-UX-P1-T1 (D #30631): el mismo producto puede ir en varias lineas, cada una con su nota. El catalogo suma a
// la linea SIN nota de ese producto; "Mi pedido" cambia, quita o separa por LINEA (lid), no por producto.
function addToCart(product) { agregarUnidad(cart, product); }

export async function renderCarrito(container) {
  currentTab = 'productos';
  cart = [];
  const bizId = getSelectedBusiness() || '';
  currentBizId = bizId; // KV2: para los listeners modulo-level del aside
  const esNegocio = isBusinessUser();

  // Titulo en navbar (visible solo en role-client via CSS).
  setPageTitle(esNegocio ? 'Carrito de Ventas' : '');

  // Restriccion negocio: requiere operador identificado.
  if (esNegocio && !getOperador()) {
    renderBloqueoOperadorCarrito(container);
    // KIOSK-FIX (bit #2977): guard por ruta — listener {once} zombi pisaba la vista activa
    // (p.ej. #/argemiro-kiosk) al confirmar PIN desde otra seccion. Ver InventarioView.
    const onOp = () => {
      if (!location.hash.startsWith('#/carrito')) return;
      if (getOperador()) renderCarrito(container);
    };
    window.addEventListener('operador:changed', onOp, { once: true });
    return;
  }

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header" style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">
        <div>
          ${regresoHtml()}
          <h1>Carrito de Ventas</h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px">Arma pedidos con productos sincronizados de Nexo Desktop.</p>
        </div>
        <div id="nx-cart-op-header"></div>
      </div>

      <div id="nx-offline-banner"></div>

      <!-- KV2 mockup v2: layout 2 columnas en desktop — catalogo (categorias->productos +
           recientes) a la izquierda, PEDIDO LATERAL FIJO a la derecha. En mobile el aside
           se oculta (CSS) y sigue el patron barra flotante + drawer. -->
      <div id="nx-cart-content" class="kv2-layout" style="margin-top:12px">
        <div id="nx-cart-main"></div>
        <aside id="nx-cart-aside" class="kv2-aside" aria-label="Pedido actual"></aside>
      </div>
    </section>`;

  renderOpsOperatorHeader('nx-cart-op-header');
  window.addEventListener('operador:changed', () => renderOpsOperatorHeader('nx-cart-op-header'));
  renderOfflineBanner();
  window.addEventListener('online',  renderOfflineBanner);
  window.addEventListener('offline', renderOfflineBanner);
  window.addEventListener('nexo-offline-queue-change', renderOfflineBanner);

  const $content = document.getElementById('nx-cart-main');

  $content.innerHTML = '<p class="empty-state">Cargando productos...</p>';

  // T6 PRODUCTOS-SYNC: intentar REST canon /api/v1/productos primero. Si la
  // tabla nexo_business.productos aun no fue aplicada al VPS o el endpoint
  // devuelve error/lista vacia, cae al sync legacy /api/sync/productos. La
  // shape la unifica ProductosApiClient.adaptCanonItem para que renderProductos
  // no requiera saber de donde vinieron los datos.
  let productos = [];
  let productosSource = null;
  if (OfflineQueue.isOnline()) {
    try {
      // CART-1 (bit #2995): antes pedia pageSize=500 -> ProductosService lo reseteaba
      // a 25 EN SILENCIO y el grid mostraba 25 de 96 productos. listarTodos pagina
      // de a 200 (tope del backend) hasta traer el catalogo completo.
      const restRes = await ProductosApiClient.listarTodos({ businessId: bizId, activo: true });
      if (restRes.ok && Array.isArray(restRes.data?.items) && restRes.data.items.length > 0) {
        productos = restRes.data.items;
        productosSource = 'rest-canon';
      }
    } catch { /* falla -> usar legacy */ }

    if (productos.length === 0) {
      const res = await fetchSyncData('productos', { businessId: bizId, pageSize: 200 });
      const rawItems = res.data?.data?.items || [];
      productos = rawItems.map(extractProduct);
      productosSource = 'sync-legacy';
    }
    productos = productos.filter(p => p.precio > 0).sort((a, b) => a.nombre.localeCompare(b.nombre));

    // Cachear catalogo para modo offline (T2)
    if (productos.length > 0) {
      OfflineQueue.cacheList('productos', bizId || 'default', productos).catch(() => {});
    }
    // T6: dejar fuente accesible para debug en consola (no UI).
    try { window.__nxLastProductosSource = productosSource; } catch {}
  } else {
    // Modo offline — usar cache
    const cached = await OfflineQueue.getCachedList('productos', bizId || 'default');
    productos = (cached || []).filter(p => p.precio > 0)
      .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
  }

  // Intento de drenar cola si recuperamos conexion
  if (OfflineQueue.isOnline()) flushPendingOrders(bizId).catch(() => {});

  if (productos.length === 0) {
    $content.innerHTML = `<div class="empty-state" style="padding:40px;text-align:center"><div style="font-size:2rem;margin-bottom:8px">\uD83D\uDED2</div><p>${OfflineQueue.isOnline() ? 'Sin productos sincronizados para este negocio.' : 'Sin productos en cache offline para este negocio.'}</p><p style="font-size:0.875rem;color:#6e7681">${OfflineQueue.isOnline() ? 'Ejecuta la sincronizacion desde Nexo Desktop.' : 'Conectate a internet al menos una vez para cachear el catalogo.'}</p></div>`;
    return;
  }

  const grupos = [...new Set(productos.map(p => p.grupo).filter(Boolean))].sort();

  // KV2 mockup v2: entrada por categorias; el buscador salta directo a productos.
  catalogoModo = grupos.length > 0 ? 'categorias' : 'productos';
  categoriaActiva = grupos.length > 0 ? null : '';
  renderProductos($content, productos, grupos, bizId);
  renderAside(bizId);
}

// KV2 mockup v2 (c): pedido lateral SIEMPRE visible en desktop. Reusa renderPedido
// (mismo markup/binds que el drawer mobile — binds scoped a su contenedor).
function renderAside(bizId) {
  const $aside = document.getElementById('nx-cart-aside');
  if (!$aside) return;
  $aside.innerHTML = `<div class="kv2-aside__inner"><h2 class="kv2-aside__title">🛒 Pedido actual</h2><div id="nx-cart-aside-body"></div></div>`;
  renderPedido($aside.querySelector('#nx-cart-aside-body'), bizId);
}

// KV2 mockup v2: bizId del render vigente (lo usan los listeners modulo-level del aside).
let currentBizId = '';

function updateBadge() {
  const badge = document.getElementById('nx-cart-badge');
  const { units } = getCartSummary();
  if (badge) {
    badge.textContent = units;
    badge.style.display = units > 0 ? 'inline' : 'none';
    if (units > 0) {
      // pulse animation trigger
      badge.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }],
        { duration: 220, easing: 'ease-out' }
      );
    }
  }
  // Actualizar barra flotante mobile si esta visible
  const $bar = document.getElementById('nx-cart-floating-bar');
  if ($bar) renderFloatingBar(currentBizId);
  // KV2: el pedido lateral fijo se mantiene en sync con cualquier cambio del cart.
  window.dispatchEvent(new CustomEvent('nx-cart:changed'));
}

// Listener UNICO modulo-level (no dentro de renders — evita acumular listeners por visita):
// refresca el aside cuando el cart cambia desde el catalogo o el drawer.
window.addEventListener('nx-cart:changed', () => {
  const $asideBody = document.getElementById('nx-cart-aside-body');
  if ($asideBody) renderPedido($asideBody, currentBizId);
});

function renderTab($el, tab, productos, grupos, bizId) {
  if (tab === 'productos') renderProductos($el, productos, grupos, bizId);
  else renderPedido($el, bizId);
}

function renderProductos($el, productos, grupos, bizId) {
  let searchTerm = '';
  let debounceTimer = null;

  // KV2 mockup v2 (a)+(b): sin buscador -> navegacion por categorias; con buscador ->
  // resultados GLOBALES directos (el buscador "salta" la categoria activa).
  function getFiltered() {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return productos.filter(p =>
        String(p.nombre).toLowerCase().includes(q) ||
        String(p.id).toLowerCase().includes(q)
      );
    }
    if (categoriaActiva === '(sin grupo)') return productos.filter(p => !p.grupo);
    return categoriaActiva ? productos.filter(p => p.grupo === categoriaActiva) : productos;
  }

  // Grid de tarjetas de categoria (entrada del catalogo): nombre + conteo por seccion.
  function renderCategorias($grid, $count) {
    const conteos = new Map();
    for (const p of productos) {
      const g = p.grupo || '(sin grupo)';
      conteos.set(g, (conteos.get(g) || 0) + 1);
    }
    if ($count) $count.textContent = `${grupos.length} categoria(s) · ${productos.length} producto(s)`;
    const cards = [
      `<button type="button" class="kv2-cat-card kv2-cat-card--all nx-cat-open" data-cat="">
         <span class="kv2-cat-card__icon">🗂️</span>
         <span class="kv2-cat-card__name">Todos</span>
         <span class="kv2-cat-card__count">${productos.length} productos</span>
       </button>`,
      ...grupos.map(g => `
        <button type="button" class="kv2-cat-card nx-cat-open" data-cat="${esc(g)}">
          <span class="kv2-cat-card__icon">🏷️</span>
          <span class="kv2-cat-card__name">${esc(g)}</span>
          <span class="kv2-cat-card__count">${conteos.get(g) || 0} producto(s)</span>
        </button>`)
    ];
    const sinGrupo = conteos.get('(sin grupo)');
    if (sinGrupo) {
      cards.push(`<button type="button" class="kv2-cat-card nx-cat-open" data-cat="(sin grupo)">
        <span class="kv2-cat-card__icon">📦</span>
        <span class="kv2-cat-card__name">(sin grupo)</span>
        <span class="kv2-cat-card__count">${sinGrupo} producto(s)</span>
      </button>`);
    }
    $grid.innerHTML = cards.join('');
    $grid.querySelectorAll('.nx-cat-open').forEach(btn => btn.addEventListener('click', () => {
      categoriaActiva = btn.dataset.cat === '(sin grupo)' ? '(sin grupo)' : btn.dataset.cat;
      catalogoModo = 'productos';
      renderList();
    }));
  }

  function renderList() {
    const $grid = document.getElementById('nx-cart-grid');
    const $count = document.getElementById('nx-cart-count');
    const $crumb = document.getElementById('nx-cart-crumb');
    if (!$grid) return;

    // Modo CATEGORIAS: solo cuando no hay busqueda activa.
    if (!searchTerm && catalogoModo === 'categorias' && grupos.length > 0) {
      if ($crumb) $crumb.innerHTML = '';
      $grid.classList.add('kv2-grid--cats');
      renderCategorias($grid, $count);
      return;
    }
    $grid.classList.remove('kv2-grid--cats');

    // Breadcrumb: volver a categorias (solo en modo productos sin busqueda).
    if ($crumb) {
      $crumb.innerHTML = (!searchTerm && grupos.length > 0) ? `
        <button type="button" id="nx-cat-back" class="btn-ghost btn-sm" style="font-size:0.875rem">&larr; Categorias</button>
        <span class="task-tag" style="font-size:0.875rem">${esc(categoriaActiva === '' ? 'Todos' : (categoriaActiva || 'Todos'))}</span>`
        : (searchTerm ? `<span style="font-size:0.875rem;color:#8b949e">Resultados globales para "${esc(searchTerm)}"</span>` : '');
      document.getElementById('nx-cat-back')?.addEventListener('click', () => {
        catalogoModo = 'categorias';
        categoriaActiva = null;
        renderList();
      });
    }

    const filtered = getFiltered();
    if ($count) $count.textContent = `${filtered.length} producto(s)`;

    if (filtered.length === 0) {
      $grid.innerHTML = `<p class="empty-state" style="grid-column:1/-1">Sin resultados${searchTerm ? ` para "${esc(searchTerm)}"` : ''}</p>`;
      return;
    }

    $grid.innerHTML = filtered.map(p => {
      const enCarrito = unidadesDe(cart, p.id);
      const inCart = enCarrito > 0 ? { qty: enCarrito } : null;
      // Hallazgo 8: un producto de receta no lleva stock propio (regla del Desktop); no se dice "Sin stock".
      const stock = estadoDeStock(p);
      const stockChip = stock === 'con'
        ? `<span style="color:#238636;font-weight:600">Stock: ${p.stock}</span>`
        : stock === 'sin' ? `<span style="color:#d29922" data-cart-sin-stock>Sin stock</span>` : '';
      const qtyBlock = inCart
        ? `<div class="ops-qty" role="group" aria-label="Cantidad">
             <button type="button" class="nx-cart-minus" data-pid="${esc(p.id)}" aria-label="Disminuir">&minus;</button>
             <span class="ops-qty__val">${inCart.qty}</span>
             <button type="button" class="nx-cart-plus" data-pid="${esc(p.id)}" aria-label="Aumentar">+</button>
           </div>`
        : `<button type="button" class="ops-cta nx-cart-add" data-pid="${esc(p.id)}">+ Agregar</button>`;
      return `
        <article class="ops-product-card ${inCart ? 'is-in-cart' : ''}" data-pid="${esc(p.id)}">
          <div class="ops-product-card__name">${highlight(p.nombre, searchTerm)}</div>
          <div class="ops-product-card__meta">
            ${p.grupo ? `<span class="task-tag" style="font-size:0.875rem">${esc(p.grupo)}</span>` : ''}
            ${stockChip}
            ${p.id ? `<span style="color:#484f58;font-size:0.875rem">Cod: ${highlight(String(p.id), searchTerm)}</span>` : ''}
          </div>
          <div class="ops-product-card__row">
            <span class="ops-product-card__price">${fmt(p.precio)}</span>
            ${qtyBlock}
          </div>
        </article>`;
    }).join('');

    // Bind buttons
    $grid.querySelectorAll('.nx-cart-add').forEach(btn => {
      btn.addEventListener('click', () => {
        const p = productos.find(x => String(x.id) === btn.dataset.pid);
        if (p) { addToCart(p); updateBadge(); renderList(); }
      });
    });
    $grid.querySelectorAll('.nx-cart-plus').forEach(btn => {
      btn.addEventListener('click', () => {
        const p = productos.find(x => String(x.id) === btn.dataset.pid);
        if (p) { addToCart(p); updateBadge(); renderList(); }
      });
    });
    $grid.querySelectorAll('.nx-cart-minus').forEach(btn => {
      btn.addEventListener('click', () => {
        const linea = cart.find(c => String(c.id) === btn.dataset.pid);
        if (linea) { quitarUnidad(cart, linea.id); updateBadge(); renderList(); }
      });
    });
  }

  // Soporte camara solo en mobile con BarcodeDetector nativo
  const hasBarcodeDetector = 'BarcodeDetector' in window;
  const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
  const showCamBtn = hasBarcodeDetector && isMobile;

  // KV2 mockup v2: buscador directo arriba (salta categorias), breadcrumb de navegacion,
  // grid (categorias o productos) y seccion PEDIDOS RECIENTES al final.
  $el.innerHTML = `
    <div class="ops-sticky-top">
      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:8px">
        <input id="nx-cart-search" type="search" class="ops-search"
               placeholder="Buscar producto por nombre o codigo…"
               autocomplete="off" autofocus inputmode="search"
               style="flex:1 1 200px;min-width:200px">
        ${showCamBtn ? `<button type="button" id="nx-cart-scan" class="ops-cta ops-cta--secondary" title="Escanear con camara" style="min-width:44px">&#128247;</button>` : ''}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;font-size:0.875rem;color:#8b949e;flex-wrap:wrap">
        <div id="nx-cart-crumb" style="display:flex;align-items:center;gap:8px;min-height:24px"></div>
        <span id="nx-cart-count">${productos.length} producto(s)</span>
        <span id="nx-cart-scan-feedback" style="min-height:1em"></span>
      </div>
    </div>

    <div id="nx-cart-grid" class="ops-card-grid"></div>

    <div id="nx-cart-recientes" style="margin-top:18px"></div>

    <div id="nx-cart-floating-bar"></div>
  `;

  // Inicial
  renderList();
  renderFloatingBar(bizId);
  renderRecientes(bizId);

  // Fix bug vaciar pedido: cuando se limpia cart desde el overlay "Mi Pedido" (otro
  // closure distinto), re-renderizamos la lista de productos para que los que tenian
  // cantidad visible ("-/+/qty") vuelvan al estado "+ Agregar".
  window.addEventListener('nx-cart:cleared', renderList);

  // T1 scanner: devuelve el producto que matchea un codigo exacto (id/Codigo)
  // o null si no existe. Se usa en USB wedge y en camara.
  function findByBarcode(code) {
    if (!code) return null;
    const norm = String(code).trim();
    if (!norm) return null;
    return productos.find(p => String(p.id).trim() === norm) || null;
  }

  function showScanFeedback(msg, color) {
    const $fb = document.getElementById('nx-cart-scan-feedback');
    if (!$fb) return;
    $fb.textContent = msg;
    $fb.style.color = color || '#8b949e';
    clearTimeout(showScanFeedback._t);
    showScanFeedback._t = setTimeout(() => {
      if ($fb) { $fb.textContent = ''; $fb.style.color = '#8b949e'; }
    }, 2500);
  }

  function handleScanAdd(code, source /* 'usb'|'cam' */) {
    const now = Date.now();
    if (now - lastScanAt < SCAN_DUP_WINDOW_MS) return false;  // rafaga duplicada
    lastScanAt = now;
    const p = findByBarcode(code);
    if (!p) {
      showScanFeedback(`Codigo ${code} no encontrado`, '#f85149');
      return false;
    }
    addToCart(p);
    updateBadge();
    searchTerm = '';
    const $s = document.getElementById('nx-cart-search');
    if ($s) $s.value = '';
    renderList();
    showScanFeedback(`+ ${p.nombre} (${source === 'cam' ? 'camara' : 'scanner'})`, '#3fb950');
    // beep opcional (sin assets)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.frequency.value = 1200;
      g.gain.setValueAtTime(0.08, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.start(); osc.stop(ctx.currentTime + 0.12);
    } catch { /* silent */ }
    return true;
  }

  // Busqueda con debounce 300ms + deteccion Enter como codigo de barras
  const $search = document.getElementById('nx-cart-search');
  $search?.addEventListener('input', (ev) => {
    clearTimeout(debounceTimer);
    const val = ev.target.value.trim();
    debounceTimer = setTimeout(() => {
      searchTerm = val;
      renderList();
    }, 300);
  });
  $search?.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    const val = String(ev.target.value || '').trim();
    // Heuristica: scanner USB entrega digitos 8-14 (EAN-8..EAN-13..UPC-A..CODE128 num)
    if (/^\d{8,14}$/.test(val)) {
      ev.preventDefault();
      handleScanAdd(val, 'usb');
    }
  });

  // T1 camara: modal con BarcodeDetector nativo
  document.getElementById('nx-cart-scan')?.addEventListener('click', async () => {
    await openCameraScanner((code) => handleScanAdd(code, 'cam'));
  });
}

/**
 * Abre un modal con la camara y BarcodeDetector.
 * onDetected(code) se invoca al primer match; si devuelve true, cierra el modal.
 */
async function openCameraScanner(onDetected) {
  if (!('BarcodeDetector' in window)) {
    avisar('Tu navegador no soporta BarcodeDetector. Usa un scanner USB o actualiza el navegador.', { tono: 'error' });
    return;
  }
  const overlay = document.createElement('div');
  overlay.className = 'ops-scan-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;z-index:9999;padding:12px';
  overlay.innerHTML = `
    <div style="background:#0d1117;border:1px solid #30363d;border-radius:10px;padding:12px;max-width:92vw;width:420px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
        <strong style="font-size:0.9rem">Escanear codigo</strong>
        <button type="button" id="nx-scan-close" class="btn-ghost btn-sm">Cerrar</button>
      </div>
      <video id="nx-scan-video" playsinline autoplay muted
             style="width:100%;max-height:60vh;background:#000;border-radius:6px"></video>
      <p style="font-size:0.875rem;color:#8b949e;margin:6px 0 0">Apunta al codigo de barras. El modal se cerrara al detectarlo.</p>
    </div>`;
  document.body.appendChild(overlay);
  const $video = overlay.querySelector('#nx-scan-video');
  const $close = overlay.querySelector('#nx-scan-close');
  let stream = null, rafId = null, stopped = false;
  const stop = () => {
    stopped = true;
    if (rafId) cancelAnimationFrame(rafId);
    if (stream) stream.getTracks().forEach(t => t.stop());
    overlay.remove();
  };
  $close.addEventListener('click', stop);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) stop(); });
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' }, audio: false
    });
    $video.srcObject = stream;
    const detector = new window.BarcodeDetector({
      formats: ['ean_13', 'ean_8', 'code_128', 'upc_a', 'upc_e']
    });
    const tick = async () => {
      if (stopped) return;
      try {
        const hits = await detector.detect($video);
        if (hits && hits.length) {
          const code = hits[0].rawValue;
          const ok = onDetected(code);
          if (ok) { stop(); return; }
        }
      } catch { /* frame error, silencioso */ }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  } catch (err) {
    avisar('No se pudo acceder a la camara: ' + (err?.message || 'permiso denegado'), { tono: 'error' });
    stop();
  }
}

/**
 * Barra flotante SIEMPRE visible (mobile y desktop) con resumen del pedido.
 * Al tocarla abre un drawer/bottom-sheet con el detalle completo — sin salir
 * de la vista de productos. Permite seguir buscando mientras se revisa el pedido.
 */
function renderFloatingBar(bizId) {
  const $bar = document.getElementById('nx-cart-floating-bar');
  if (!$bar) return;
  const { items, units, total } = getCartSummary();
  if (items === 0) { $bar.innerHTML = ''; return; }
  $bar.innerHTML = `
    <div class="ops-cart-bar ops-cart-bar--always" id="nx-cart-open-drawer" role="button" tabindex="0" aria-label="Ver pedido">
      <div style="display:flex;align-items:center;gap:10px">
        <span class="ops-cart-bar__count">${units}</span>
        <span>Ver pedido (${items} ${items === 1 ? 'item' : 'items'})</span>
      </div>
      <strong>${fmt(total)}</strong>
    </div>`;
  document.getElementById('nx-cart-open-drawer')?.addEventListener('click', () => openCartDrawer(bizId));
}

/**
 * Abre un drawer (bottom-sheet en mobile, modal en desktop) con el detalle
 * del pedido. NO navega fuera de la vista de productos.
 */
function openCartDrawer(bizId) {
  // Evitar duplicados
  document.getElementById('nx-cart-drawer-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'nx-cart-drawer-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.65);z-index:9998;display:flex;align-items:flex-end;justify-content:center;';
  // Desktop: centrar vertical; Mobile: bottom-sheet
  if (window.matchMedia('(min-width: 768px)').matches) {
    overlay.style.alignItems = 'center';
  }

  const panel = document.createElement('div');
  panel.id = 'nx-cart-drawer-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Mi pedido');
  panel.style.cssText = `
    background:#0d1117;border-top:2px solid #30363d;border-radius:16px 16px 0 0;
    width:100%;max-width:800px;max-height:85vh;overflow:auto;
    padding:16px;box-shadow:0 -8px 24px rgba(0,0,0,0.6);
  `;
  if (window.matchMedia('(min-width: 768px)').matches) {
    panel.style.borderRadius = '12px';
    panel.style.border = '1px solid #30363d';
    panel.style.margin = '0 16px';
  }

  panel.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
      <h2 style="margin:0;font-size:1.1rem">Mi Pedido</h2>
      <button id="nx-cart-drawer-close" class="btn-ghost" aria-label="Cerrar"
              style="padding:4px 12px;font-size:1.2rem;line-height:1">&times;</button>
    </div>
    <div id="nx-cart-drawer-content"></div>
  `;
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  // Renderizar el detalle del pedido en el drawer (reusa renderPedido)
  renderPedido(document.getElementById('nx-cart-drawer-content'), bizId);

  // Cerrar: click fuera, boton X, Escape
  const closeDrawer = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeDrawer(); });
  document.getElementById('nx-cart-drawer-close')?.addEventListener('click', closeDrawer);
  const escHandler = (e) => { if (e.key === 'Escape') { closeDrawer(); document.removeEventListener('keydown', escHandler); } };
  document.addEventListener('keydown', escHandler);
}

function renderPedido($el, bizId) {
  const { items, units, total } = getCartSummary();

  if (items === 0) {
    $el.innerHTML = `<div class="empty-state" style="padding:40px;text-align:center"><div style="font-size:2rem;margin-bottom:8px">\uD83D\uDED2</div><p>Tu pedido esta vacio.</p><p style="font-size:0.875rem;color:#6e7681">Agrega productos desde la pestana Productos.</p></div>`;
    return;
  }

  $el.innerHTML = `
    <div class="detail-section">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <h2 style="margin:0;font-size:1rem">Mi Pedido</h2>
        <button type="button" class="btn-ghost btn-sm nx-ped-clear" style="font-size:0.875rem;color:#f85149">Vaciar pedido</button>
      </div>

      <div class="banco-stats-grid" style="margin-bottom:12px">
        <div class="banco-stat-card"><div class="banco-stat-value">${items}</div><div class="banco-stat-label">Productos</div></div>
        <div class="banco-stat-card"><div class="banco-stat-value">${units}</div><div class="banco-stat-label">Unidades</div></div>
        <div class="banco-stat-card"><div class="banco-stat-value" style="color:#238636">${fmt(total)}</div><div class="banco-stat-label">Total</div></div>
      </div>

      <div style="background:#161b22;border:1px solid #30363d;border-radius:8px;overflow:hidden">
        <div class="kv2-order-row" style="display:grid;grid-template-columns:1fr 90px 100px 90px 40px;gap:0;padding:10px 14px;background:#21262d;font-size:0.875rem;font-weight:700;color:#8b949e;text-transform:uppercase">
          <div>Producto</div><div style="text-align:center">Precio</div><div style="text-align:center">Cantidad</div><div style="text-align:right">Subtotal</div><div></div>
        </div>
        ${cart.map(c => `
          <div data-cart-linea data-lid="${esc(c.lid)}" style="border-bottom:1px solid #21262d">
          <div class="kv2-order-row" style="display:grid;grid-template-columns:1fr 90px 100px 90px 40px;gap:0;padding:8px 14px 4px;align-items:center">
            <div style="font-weight:600;font-size:0.875rem">${esc(c.nombre)}</div>
            <div style="text-align:center;font-size:0.875rem;color:#8b949e">${fmt(c.precio)}</div>
            <div style="text-align:center">
              <div style="display:flex;align-items:center;justify-content:center;gap:4px">
                <button class="btn-ghost btn-sm nx-ped-minus" data-lid="${esc(c.lid)}" style="font-size:0.875rem;padding:1px 6px">-</button>
                <input type="text" inputmode="numeric" class="nx-ped-qty" data-lid="${esc(c.lid)}" value="${c.qty}" style="width:40px;padding:3px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem;text-align:center">
                <button class="btn-ghost btn-sm nx-ped-plus" data-lid="${esc(c.lid)}" style="font-size:0.875rem;padding:1px 6px">+</button>
              </div>
            </div>
            <div style="text-align:right;font-weight:700;font-size:0.875rem;color:#d29922">${fmt(c.precio * c.qty)}</div>
            <div style="text-align:center"><button class="btn-ghost btn-sm nx-ped-del" data-lid="${esc(c.lid)}" style="font-size:0.875rem;color:#f85149;padding:1px 6px">&times;</button></div>
          </div>
          <div data-cart-qty-aviso role="alert" hidden style="padding:0 14px 6px;font-size:0.875rem;color:#d29922"></div>
          <!-- KV2-T3: nota por producto -->
          <div style="padding:0 14px 8px">
            <input type="text" class="nx-ped-nota" data-lid="${esc(c.lid)}" value="${esc(c.nota || '')}" maxlength="120"
                   placeholder="📝 Nota (ej: sin cebolla, término medio…)" aria-label="Nota de ${esc(c.nombre)}"
                   style="width:100%;padding:4px 8px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#c9d1d9;font-size:14px">
            ${c.qty > 1 ? `<button type="button" class="btn-ghost btn-sm nx-ped-separar" data-lid="${esc(c.lid)}" style="font-size:14px;margin-top:4px">Separar 1 con otra nota</button>` : ''}
          </div>
          </div>
        `).join('')}
        <div class="kv2-order-row" style="display:grid;grid-template-columns:1fr 90px 100px 90px 40px;gap:0;padding:10px 14px;background:#21262d">
          <div style="font-weight:700;font-size:0.875rem">Total</div><div></div><div style="text-align:center;font-weight:700">${units}</div>
          <div style="text-align:right;font-weight:700;font-size:0.9rem;color:#238636">${fmt(total)}</div><div></div>
        </div>
      </div>

      <!-- KV2-T3: nota general del pedido -->
      <div style="margin-top:12px">
        <label style="display:block;font-size:0.875rem;color:#8b949e;font-weight:600;margin-bottom:4px">Nota general del pedido</label>
        <textarea id="nx-cart-nota-general" rows="2" maxlength="240"
                  placeholder="Ej: para las 8pm, mesa junto a la ventana, pagan con tarjeta…"
                  style="width:100%;padding:6px 10px;background:#0d1117;border:1px solid #30363d;border-radius:6px;color:#c9d1d9;font-size:0.875rem;resize:vertical">${esc(pedidoNota)}</textarea>
      </div>

      ${renderMesaSelector()}
      <div data-cart-mesa-estado role="status" aria-live="polite">${estadoDeMesaHTML(bizId)}</div>

      <div style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end">
        <button type="button" class="btn-ghost btn-sm nx-ped-download" style="font-size:0.875rem">Descargar</button>
        <button type="button" class="btn-primary btn-sm nx-ped-send" style="font-size:0.875rem;background:#238636">Enviar pedido</button>
      </div>
    </div>`;

  // KV2-T2: mini-teclado numerico de mesa + "Llevar" (1 toque). Enter confirma.
  bindMesaSelector($el, bizId);

  // Bind events
  $el.querySelectorAll('.nx-ped-plus').forEach(btn => btn.addEventListener('click', () => {
    const item = lineaPorLid(cart, btn.dataset.lid);
    if (item) { item.qty++; updateBadge(); renderPedido($el, bizId); }
  }));
  $el.querySelectorAll('.nx-ped-minus').forEach(btn => btn.addEventListener('click', () => {
    const item = lineaPorLid(cart, btn.dataset.lid);
    if (item) { cantidadDeLinea(cart, item.lid, item.qty - 1); updateBadge(); renderPedido($el, bizId); }
  }));
  $el.querySelectorAll('.nx-ped-del').forEach(btn => btn.addEventListener('click', () => {
    quitarLinea(cart, btn.dataset.lid);
    updateBadge();
    renderPedido($el, bizId);
  }));
  // CARRITO-UX-P1-T1 (D #30631): una unidad pasa a su propia linea, sin nota, y el foco queda en su nota.
  $el.querySelectorAll('.nx-ped-separar').forEach(btn => btn.addEventListener('click', () => {
    const nuevo = separarUnidad(cart, btn.dataset.lid);
    if (!nuevo) return;
    renderPedido($el, bizId);
    $el.querySelector(`.nx-ped-nota[data-lid="${CSS.escape(nuevo)}"]`)?.focus();
  }));
  $el.querySelectorAll('.nx-ped-qty').forEach(input => input.addEventListener('change', () => {
    // CARRITO-UX-P1-T1: una cantidad invalida ya no borra la linea en silencio (antes parseInt || 0 -> quitar).
    const item = lineaPorLid(cart, input.dataset.lid);
    const { qty, error } = interpretarCantidad(input.value, item ? item.qty : 1);
    const $aviso = input.closest('[data-cart-linea]')?.querySelector('[data-cart-qty-aviso]');
    if (error) {
      input.value = String(qty);
      if ($aviso) { $aviso.textContent = error; $aviso.hidden = false; }
      input.focus();
      return;
    }
    cantidadDeLinea(cart, input.dataset.lid, qty);
    updateBadge();
    renderPedido($el, bizId);
  }));
  // KV2-T3: nota por producto (guarda en cart[i].nota sin re-render para no perder foco).
  $el.querySelectorAll('.nx-ped-nota').forEach(input => input.addEventListener('input', () => {
    const item = lineaPorLid(cart, input.dataset.lid);
    if (item) item.nota = input.value;
  }));
  // KV2-T3: nota general del pedido.
  $el.querySelector('#nx-cart-nota-general')?.addEventListener('input', (e) => { pedidoNota = e.target.value; });

  // KV2: binds SCOPED al contenedor ($el) — el pedido vive simultaneamente en el aside
  // fijo (desktop) y en el drawer (mobile); ids duplicados colgarian los botones
  // (leccion FIX modal inventario #2050/#143).
  $el.querySelector('.nx-ped-clear')?.addEventListener('click', () => {
    cart = [];
    updateBadge();
    renderPedido($el, bizId);
    // Dispara re-render de la lista de productos (closure de renderProductos lo escucha)
    window.dispatchEvent(new CustomEvent('nx-cart:cleared'));
  });

  $el.querySelector('.nx-ped-download')?.addEventListener('click', () => {
    const orden = buildOrden(bizId);
    const blob = new Blob([JSON.stringify(orden, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedido_${new Date().toISOString().slice(0, 10)}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  });

  $el.querySelector('.nx-ped-send')?.addEventListener('click', () => {
    // SEC-SYNC-NEGOCIO-T1 (#15532): sin negocio real no se envia ni se encola; el carrito se queda.
    const negocio = negocioDeLaSubida(bizId);
    if (!negocio) { avisarSinNegocio($el); return; }
    // KV2-T2: exigir mesa (numero) o "Llevar" antes de enviar. CARRITO-SIN-NATIVOS: aviso en la pantalla, sin alert.
    if (!mesaSel) { avisarEnPedido($el, MENSAJE_SIN_MESA); return; }
    // CARRITO-SIN-NATIVOS (SI #29875): la confirmacion se ve en la pantalla, sin confirm nativo.
    const { items, units, total } = getCartSummary();
    const op = getOperador();
    const lineas = lineasDeConfirmacion({ mesa: mesaSel, vendedor: op?.nombre || AppConfig.role || 'desconocido', items, units, total: fmt(total) });
    // CARRITO-MESAS-ADICIONES-T1: si la mesa ya tiene cuenta abierta, la confirmacion dice a donde va este pedido.
    const abierta = mesaAbierta(mesasCache.biz === bizId ? mesasCache.mesas : null, mesaSel);
    if (abierta) lineas.filas.push(lineaDeMesa(abierta, fmt).nota);
    // Paso 3 (D, nexo_desktop 514): la cuenta se fija AL CONFIRMAR, no al enviar: lo que vio el mesero es lo que viaja.
    const destino = adicionDe(abierta);
    mostrarConfirmacion($el, lineas, () => enviarPedido($el, bizId, negocio, destino));
  });
}

/** CARRITO-SIN-NATIVOS: el envio de siempre (mismo buildOrden, mismo /api/sync/upload), ya confirmado en la pantalla. */
async function enviarPedido($el, bizId, negocio, destino = adicionDe(null)) {
  {
    const btn = $el.querySelector('.nx-ped-send');
    btn.disabled = true;
    btn.textContent = 'Enviando...';
    const orden = buildOrden(negocio, destino);
    const payload = {
      table: 'ordenes',
      business_id: negocio,
      rows: [orden],
      row_count: 1,
      synced_at: new Date().toISOString()
    };
    mesaSel = null; // KV2-T2: la mesa ya viaja en el payload; limpiar para el proximo pedido
    pedidoNota = ''; // KV2-T3: la nota general ya viaja en el payload

    // T2: si estamos offline, encolar y avisar
    if (!OfflineQueue.isOnline()) {
      await OfflineQueue.enqueue({
        tipo: 'carrito',
        businessId: negocio,
        endpoint: '/api/sync/upload',
        payload
      });
      btn.textContent = 'Encolado (offline)';
      btn.style.background = '#d29922';
      cart = [];
      updateBadge();
      pushReciente(bizId, orden, 'encolado');   // KV2: pedidos recientes
      renderRecientes(bizId);
      setTimeout(() => renderPedido($el, bizId), 2000);
      renderOfflineBanner();
      return;
    }

    try {
      const result = await ApiBase.post('/api/sync/upload', payload);
      if (result.ok) {
        btn.textContent = 'Pedido enviado';
        btn.style.background = '#238636';
        cart = [];
        updateBadge();
        pushReciente(bizId, orden, 'enviado');  // KV2: pedidos recientes
        renderRecientes(bizId);
        setTimeout(() => renderPedido($el, bizId), 2000);
      } else {
        // Fallback offline si se pierde red durante el POST
        await OfflineQueue.enqueue({
          tipo: 'carrito',
          businessId: negocio,
          endpoint: '/api/sync/upload',
          payload
        });
        btn.textContent = 'Encolado (red inestable)';
        btn.style.background = '#d29922';
        cart = [];
        updateBadge();
        pushReciente(bizId, orden, 'encolado');
        renderRecientes(bizId);
        setTimeout(() => renderPedido($el, bizId), 2000);
        renderOfflineBanner();
      }
    } catch {
      await OfflineQueue.enqueue({
        tipo: 'carrito',
        businessId: negocio,
        endpoint: '/api/sync/upload',
        payload
      });
      btn.textContent = 'Encolado';
      btn.style.background = '#d29922';
      cart = [];
      updateBadge();
      pushReciente(bizId, orden, 'encolado');
      renderRecientes(bizId);
      setTimeout(() => renderPedido($el, bizId), 2000);
      renderOfflineBanner();
    }
  }
}

// ===== KV2 mockup v2 (d): PEDIDOS RECIENTES (hoy) =====
// Pinta primero con el estado local (enviado/encolado) y luego cruza con
// GET /api/sync/ordenes: si la orden esta en el server -> "En la nube" (el Desktop
// la toma en su proximo poll). El estado "ya descargada por Desktop" requiere un
// flag que sync_ordenes NO expone hoy (gap documentado, se agrega sin re-diseno).
async function renderRecientes(bizId) {
  const $sec = document.getElementById('nx-cart-recientes');
  if (!$sec) return;
  const recientes = loadRecientes(bizId);
  if (!recientes.length) { $sec.innerHTML = ''; return; }

  const pinta = (cloudIds) => {
    if (!document.getElementById('nx-cart-recientes')) return; // vista desmontada
    const rows = recientes.map(r => {
      let chip;
      if (cloudIds && cloudIds.has(String(r.id))) {
        chip = `<span class="kv2-rec-chip" style="color:#3fb950;border-color:#3fb95055;background:#3fb95012" title="La orden esta en la nube; Nexo Desktop la toma en su proximo ciclo de sincronizacion">☁ En la nube</span>`;
      } else if (r.estadoLocal === 'encolado') {
        chip = `<span class="kv2-rec-chip" style="color:#d29922;border-color:#d2992255;background:#d2992212" title="Sin conexion al enviar: se sube automaticamente al reconectar">⏳ Encolada offline</span>`;
      } else if (cloudIds) {
        chip = `<span class="kv2-rec-chip" style="color:#d29922;border-color:#d2992255;background:#d2992212" title="Se envio pero aun no aparece en la nube — reintenta o revisa conexion">📤 Enviada · verificar</span>`;
      } else {
        chip = `<span class="kv2-rec-chip" style="color:#8b949e;border-color:#30363d;background:#161b22">📤 Enviada</span>`;
      }
      const mesaTxt = r.mesa === 'LLEVAR' ? '🥡 Llevar' : (r.mesa ? `Mesa ${esc(String(r.mesa))}` : '—');
      return `
        <div class="kv2-rec-row">
          <code style="font-size:0.875rem;color:#6e7681">${esc(String(r.id).replace('ORD-', '#'))}</code>
          <span style="font-size:0.875rem;color:#c9d1d9;font-weight:600">${mesaTxt}</span>
          <span style="font-size:0.875rem;color:#8b949e">${esc(r.hora || '')}</span>
          <span style="font-size:0.875rem;color:#8b949e;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(r.vendedor || '')}</span>
          <span style="font-size:0.875rem;font-weight:700;color:#d29922;text-align:right">${fmt(r.total || 0)}</span>
          ${chip}
        </div>`;
    }).join('');
    $sec.innerHTML = `
      <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="font-size:1rem">🧾</span>
          <strong style="color:#f0f6fc;font-size:0.88rem">Pedidos recientes (hoy)</strong>
          <span style="background:#8b949e22;color:#8b949e;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:600">${recientes.length}</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:6px">${rows}</div>
      </div>`;
  };

  pinta(null); // estado local inmediato
  if (OfflineQueue.isOnline()) {
    try {
      const res = await fetchSyncData('ordenes', { businessId: bizId, pageSize: 100 });
      const items = res.data?.data?.items || [];
      const cloudIds = new Set(items.map(i => String(i.rowId ?? i.row_id ?? '')));
      pinta(cloudIds);
    } catch { /* se queda el estado local */ }
  }
}

/**
 * Renderiza el chip "Operador activo" en el header de Carrito/Inventario.
 * Click → abre el modal de seleccion de operador (mismo componente que el kiosk).
 * Si no hay operador: invita a identificarse.
 */
function renderOpsOperatorHeader(elId) {
  const $el = document.getElementById(elId);
  if (!$el) return;
  const op = getOperador();
  if (op) {
    $el.innerHTML = `
      <button type="button" class="ops-operator-chip" id="${elId}-btn" title="Cambiar operador">
        <span>&#128100;</span>
        <span>${esc(op.nombre)}</span>
        <span style="opacity:0.6">(cambiar)</span>
      </button>`;
  } else {
    $el.innerHTML = `
      <button type="button" class="ops-operator-chip ops-operator-chip--empty" id="${elId}-btn" title="Identificar operador">
        <span>&#128275;</span>
        <span>Identificar operador</span>
      </button>`;
  }
  document.getElementById(`${elId}-btn`)?.addEventListener('click', () => {
    // Reutiliza el componente global del kiosk (header badge)
    const kioskBtn = document.getElementById('nx-op-badge') || document.getElementById('nx-op-identify');
    if (kioskBtn) kioskBtn.click();
  });
}

/**
 * CARRITO-MESAS-ADICIONES-T1: bajo el teclado, si la mesa tecleada tiene cuenta abierta en el escritorio, su linea
 * (desde, productos, total) y a donde va este pedido. Sin dato o mesa libre: nada (el carrito sigue como hoy).
 * Pinta con lo que hay y, si el dato venció, lo pide y repinta solo este bloque.
 */
function estadoDeMesaHTML(bizId) {
  const pinta = (mesas) => {
    const m = mesaAbierta(mesas, mesaSel);
    if (!m) return '';
    const l = lineaDeMesa(m, fmt);
    return `<div data-cart-mesa-abierta style="margin-top:8px;padding:8px 10px;background:#1f6feb12;border:1px solid #1f6feb55;border-radius:6px;font-size:0.875rem;color:#c9d1d9">
      <div style="font-weight:600">${esc(l.linea)}</div>
      <div style="color:#8b949e;font-size:0.875rem">${esc(l.nota)}</div>
      ${l.viejo ? `<div style="color:#d29922;font-size:0.875rem">${esc(l.viejo)}</div>` : ''}</div>`;
  };
  const cache = mesasCache.biz === bizId ? mesasCache.mesas : null;
  if (mesaSel && mesaSel !== 'LLEVAR' && !(mesasCache.biz === bizId && Date.now() - mesasCache.at < 30000)) {
    cargarMesas(bizId).then((mesas) => {
      document.querySelectorAll('[data-cart-mesa-estado]').forEach(($e) => { $e.innerHTML = pinta(mesas); });
    });
  }
  return pinta(cache);
}

// KV2-T2: selector NUMERICO de mesa (mini-teclado) + "Llevar" (1 toque). Diseno aprobado
// KV2-T1 (sin salon de mesas): la operaria teclea el numero de mesa o toca Llevar.
function renderMesaSelector() {
  const isLlevar = mesaSel === 'LLEVAR';
  const disp = isLlevar ? '🥡 Para llevar'
    : (mesaSel ? `Mesa ${esc(mesaSel)}` : 'Sin seleccionar');
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', '✓'];
  return `
      <div style="margin-top:14px;background:#0d1117;border:1px solid #30363d;border-radius:8px;padding:12px">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px">
          <span style="font-size:0.875rem;color:#8b949e;font-weight:600">Mesa <span style="color:#f85149">*</span></span>
          <span id="nx-mesa-disp" style="font-size:0.95rem;font-weight:700;color:${mesaSel ? '#3fb950' : '#8b949e'}">${disp}</span>
        </div>
        <div style="display:flex;gap:8px">
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:6px;flex:1">
            ${keys.map(k => `<button type="button" class="nx-mesa-key" data-key="${k}" style="padding:10px;background:#161b22;border:1px solid #30363d;border-radius:6px;color:#e1e4e8;font-size:1.05rem;font-weight:600;cursor:pointer">${k}</button>`).join('')}
          </div>
          <button type="button" class="nx-mesa-llevar" style="flex:0 0 82px;background:${isLlevar ? '#1f6feb22' : '#161b22'};border:1px solid ${isLlevar ? '#1f6feb' : '#30363d'};border-radius:6px;color:${isLlevar ? '#79c0ff' : '#c9d1d9'};font-size:0.875rem;font-weight:600;cursor:pointer;line-height:1.3">🥡<br>Llevar</button>
        </div>
      </div>`;
}

function bindMesaSelector($el, bizId) {
  $el.querySelectorAll('.nx-mesa-key').forEach(b => b.addEventListener('click', () => {
    const k = b.dataset.key;
    if (k === '⌫') { // backspace
      mesaSel = (typeof mesaSel === 'string' && mesaSel !== 'LLEVAR') ? (mesaSel.slice(0, -1) || null) : null;
    } else if (k === '✓') { /* Enter: confirma (mesaSel ya esta seteado) */ }
    else { // digito 0-9
      if (mesaSel === 'LLEVAR' || !mesaSel) mesaSel = '';
      if (String(mesaSel).length < 3) mesaSel = String(mesaSel) + k; // max 3 digitos
    }
    renderPedido($el, bizId);
  }));
  $el.querySelector('.nx-mesa-llevar')?.addEventListener('click', () => {
    mesaSel = mesaSel === 'LLEVAR' ? null : 'LLEVAR';
    renderPedido($el, bizId);
  });
}

/** SEC-SYNC-NEGOCIO-T1: el aviso va encima de los botones del pedido, sin alert nativo. */
function avisarSinNegocio($el) {
  avisarEnPedido($el, MENSAJE_SIN_NEGOCIO);
}

/** CARRITO-SIN-NATIVOS: un aviso en la pantalla, encima de los botones del pedido (reemplaza al alert). */
function avisarEnPedido($el, texto) {
  const btn = $el.querySelector('.nx-ped-send');
  if (!btn || !btn.parentElement) return;
  let $aviso = $el.querySelector('[data-cart-sin-negocio]');
  if (!$aviso) {
    $aviso = document.createElement('div');
    $aviso.setAttribute('data-cart-sin-negocio', '');
    $aviso.setAttribute('role', 'alert');
    $aviso.style.cssText = 'margin-top:12px;padding:8px 12px;font-size:0.875rem;line-height:1.4;color:#f0b72f;background:#d299221a;border:1px solid #d29922;border-radius:6px';
    btn.parentElement.insertAdjacentElement('beforebegin', $aviso);
  }
  $aviso.textContent = texto;
}

/**
 * CARRITO-SIN-NATIVOS (SI #29875): la confirmacion del envio EN LA PANTALLA, encima de los botones, en vez del
 * confirm nativo. "Enviar" corre el envio de siempre; "Volver" la cierra y el carrito queda como estaba.
 */
function mostrarConfirmacion($el, lineas, alConfirmar) {
  const btn = $el.querySelector('.nx-ped-send');
  if (!btn || !btn.parentElement) return;
  $el.querySelector('[data-cart-sin-negocio]')?.remove();
  $el.querySelector('[data-cart-confirmar]')?.remove();
  const $caja = document.createElement('div');
  $caja.setAttribute('data-cart-confirmar', '');
  $caja.setAttribute('role', 'dialog');
  $caja.setAttribute('aria-label', 'Confirmar el pedido');
  $caja.style.cssText = 'margin-top:12px;padding:10px 12px;background:#161b22;border:1px solid #1f6feb;border-radius:8px';
  $caja.innerHTML = `
    <div style="font-size:0.875rem;font-weight:700;color:#f0f6fc;margin-bottom:6px">${esc(lineas.titulo)}</div>
    <div data-cart-confirmar-lineas style="font-size:0.875rem;color:#c9d1d9;line-height:1.5">${lineas.filas.map((f) => `<div>${esc(f)}</div>`).join('')}</div>
    <div style="font-size:0.875rem;color:#8b949e;margin-top:6px">${esc(lineas.pie)}</div>
    <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:10px">
      <button type="button" class="btn-ghost btn-sm" data-cart-confirmar-volver>Volver</button>
      <button type="button" class="btn-primary btn-sm" data-cart-confirmar-enviar style="background:#238636">Enviar</button>
    </div>`;
  btn.parentElement.insertAdjacentElement('beforebegin', $caja);
  btn.disabled = true;
  const cerrar = () => { $caja.remove(); btn.disabled = false; };
  $caja.querySelector('[data-cart-confirmar-volver]').addEventListener('click', cerrar);
  $caja.querySelector('[data-cart-confirmar-enviar]').addEventListener('click', () => { $caja.remove(); alConfirmar(); });
  $caja.querySelector('[data-cart-confirmar-enviar]').focus();
}

// CARRITO-MESAS-ADICIONES-T1 paso 3: es_adicion y venta_destino (contrato de D #29967); sin cuenta abierta: false y null.
function buildOrden(bizId, destino = adicionDe(null)) {
  const now = new Date();
  const op = getOperador();
  return {
    id: `ORD-${Date.now()}`,
    fecha: now.toISOString(),
    hora: formatearFecha(now, { hour: '2-digit', minute: '2-digit' }, { parte: 'hora' }),
    usuario_panel: op?.nombre || AppConfig.role,
    operador_id: op?.id || null,   // PIN rotativo (PANEL-OPS-DASHBOARDS-001 T5)
    operador_cargo: op?.cargo || null,
    vendedor: op?.nombre || AppConfig.role || null,   // KV2-T2: vendedor explicito en payload
    mesa: (mesaSel && mesaSel !== 'LLEVAR') ? mesaSel : null,   // KV2-T2
    para_llevar: mesaSel === 'LLEVAR',                          // KV2-T2
    es_adicion: mesaSel !== 'LLEVAR' && destino.es_adicion,     // paso 3: Para llevar nunca es adicion
    venta_destino: mesaSel !== 'LLEVAR' ? destino.venta_destino : null,
    negocio_id: bizId || 'default',
    estado: 'nuevo',
    notas: (pedidoNota || '').trim(),   // KV2-T3: nota general del pedido
    // KV2-T3 + CARRITO-UX-P1-T1 (obs de Q #30657): el detalle lo arma detalleDelPedido, el mismo que prueba el guardia.
    detalle: detalleDelPedido(cart),
    total: cart.reduce((s, c) => s + c.precio * c.qty, 0),
    items_count: cart.length,
    units_count: cart.reduce((s, c) => s + c.qty, 0)
  };
}

/**
 * Banner flotante en el header con el estado de conexion + cola pendiente.
 * Se redibuja ante 'online'/'offline' y 'nexo-offline-queue-change'.
 */
async function renderOfflineBanner() {
  const $el = document.getElementById('nx-offline-banner');
  if (!$el) return;
  const online = OfflineQueue.isOnline();
  const pending = await OfflineQueue.listPending();
  const count = Array.isArray(pending) ? pending.length : 0;

  if (online && count === 0) { $el.innerHTML = ''; return; }
  if (!online) {
    $el.innerHTML = `
      <div class="ops-offline-banner" data-state="offline">
        <strong>Sin conexion.</strong> Los datos locales pueden estar desactualizados. Los envios se encolan y se sincronizaran al reconectar.
        ${count > 0 ? ` <span style="opacity:0.85">(${count} pendiente${count === 1 ? '' : 's'})</span>` : ''}
      </div>`;
    return;
  }
  // online con pendientes -> avisa + boton reintentar
  $el.innerHTML = `
    <div class="ops-offline-banner" data-state="pending">
      <strong>Sincronizando ${count} operacion${count === 1 ? '' : 'es'} pendiente${count === 1 ? '' : 's'}...</strong>
      <button type="button" id="nx-offline-retry" class="btn-ghost btn-sm" style="margin-left:8px">Reintentar ahora</button>
    </div>`;
  document.getElementById('nx-offline-retry')?.addEventListener('click', () => {
    flushPendingOrders();
  });
}

/**
 * Drena la cola de ordenes via ApiBase. Tolera errores y deja los fallidos
 * en cola con contador de intentos.
 */
async function flushPendingOrders(bizId) {
  if (!OfflineQueue.isOnline()) return;
  const sender = async ({ endpoint, payload }) => {
    try {
      const res = await ApiBase.post(endpoint, payload);
      return { ok: !!res.ok, error: res.error };
    } catch (err) {
      return { ok: false, error: err?.message || 'network' };
    }
  };
  const result = await OfflineQueue.flush(sender, bizId ? String(bizId) : undefined);
  renderOfflineBanner();
  return result;
}

// Drenado global al recuperar conexion
window.addEventListener('online', () => { flushPendingOrders().catch(() => {}); });

// Limpiar cache/cola si el operario cambia de negocio (T2: no colas cross-negocio)
window.addEventListener('business:changed', (ev) => {
  const prev = ev?.detail?.previousBusinessId;
  if (prev) { OfflineQueue.clearForBusiness(prev).catch(() => {}); }
});

/**
 * Bloqueo cuando negocio entra a Carrito sin operador identificado. Levanta el modal
 * PIN reenviando el click al chip del navbar (mismo patron que InventarioView).
 */
function renderBloqueoOperadorCarrito(container) {
  container.innerHTML = `
    <section class="business-require-auth">
      <div class="require-auth-card">
        <div class="require-auth-icon">&#128274;</div>
        <h2>Identificate para continuar</h2>
        <p>Por seguridad, debes ingresar tu PIN de operador antes de usar el carrito de ventas.</p>
        <button type="button" id="nx-cart-auth-cta" class="btn-primary">Identificarse</button>
      </div>
    </section>`;
  document.getElementById('nx-cart-auth-cta')?.addEventListener('click', () => {
    const kioskBtn = document.getElementById('nx-op-badge') || document.getElementById('nx-op-identify');
    if (kioskBtn) kioskBtn.click();
  });
}
